<template>
  <div
    class="list-row-container row"
    data-test="attachment-list-row"
  >
    <button
      type="button"
      class="row-content button-reset"
      @click="rowClick"
    >
      <div
        v-if="thumbnail"
        class="thumbnail thumbnail-img"
        role="img"
        :aria-label="`Media for ${rowAttachment.assetTitle}`"
        :style="{ backgroundImage: thumbnail }"
      >
        <attachment-progress
          v-if="attachmentState"
          :duration="rowAttachment.metadata.duration"
          :watched="attachmentState.currentTimestamp"
        />
      </div>
      <div
        v-else
        class="thumbnail"
      >
        <icon
          :name="rowAttachment.icon"
          variant="767676"
        />
        <attachment-progress
          v-if="attachmentState"
          :duration="rowAttachment.metadata.duration"
          :watched="attachmentState.currentTimestamp"
        />
      </div>
      <div class="name text-sm">
        <purchase-order-preview
          v-if="user.previewer"
          :resource="rowAttachment"
        />
        <slot
          v-if="hasSlot"
          name="name"
        />
        <template v-else>
          <div class="name-container text-truncate-2-lines">
            <lock-button
              v-if="locked"
              :resource="{ type: 'attachment', id: rowAttachment.id }"
              :source="{ type: 'Locked Attachment', value: rowAttachment.id }"
              class="lock-button"
            />
            <span class="text-sm font-medium text-primary-900">{{ name }}</span>
          </div>
        </template>
      </div>
      <div
        v-if="showPlay || rowAttachment.duration"
        class="action-container"
      >
        <div class="view">
          <icon
            v-if="showPlay"
            name="play-no-circle"
            class="icon"
          />
        </div>
        <div
          v-if="rowAttachment.duration"
          class="text-xs font-medium duration-text"
          v-html="durationText"
        />
      </div>
    </button>
    <template v-if="showBookmarkIcon">
      <button
        type="button"
        class="button-reset"
        :aria-label="isBookmarked ? 'Remove bookmark' : 'Add bookmark'"
        :aria-pressed="isBookmarked"
        @click.stop="toggleBookmark"
        @keydown.enter.stop.prevent="toggleBookmark"
        @keydown.space.stop.prevent="toggleBookmark"
      >
        <icon
          :name="isBookmarked ? 'bookmark-icon-filled' : 'bookmark-icon'"
          class="bookmark-icon"
        />
      </button>
    </template>
  </div>
</template>

<script>
import AttachmentProgress from '@components/ui/attachment-progress';
import Icon from '@components/singletons/icon';
import LockButton from '@components/singletons/lock-button';
import PurchaseOrderPreview from '@components/singletons/purchaseOrderPreview';

import Attachment from '@/classes/attachment';

export default {
  name: 'AttachmentListRow',
  components: { AttachmentProgress, Icon, LockButton, PurchaseOrderPreview },
  props: {
    attachment: {
      type: Object,
      default: null,
    },
    card: {
      type: Object,
      default: null,
    },
    source: {
      type: Object,
      default: () => ({}),
    },
    showLock: {
      type: Boolean,
      default: true,
    },
    showBookmarkIcon: {
      type: Boolean,
      default: false,
    },
    isBookmarked: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['row-clicked', 'toggle-bookmark'],
  computed: {
    attachmentState() {
      if (this.rowAttachment) {
        return this.$store.getters['attachmentStates/get'][this.rowAttachment.id];
      }
    },
    completed() {
      if (this.attachmentState) {
        return this.rowAttachment.completedState(this.attachmentState);
      }
    },
    displayAttachment() {
      if (this.rowAttachment) {
        if (this.rowAttachment.displayAttachmentId) {
          return this.$store.getters['attachments/get'][this.rowAttachment.displayAttachmentId];
        }
        return this.rowAttachment;
      }
    },
    durationText() {
      if (this.inProgress) {
        return `${this.formatDuration(this.timeRemaining)}`;
      }
      return this.rowAttachment.durationInMinutes;
    },
    hasSlot() {
      return !!this.$slots.name;
    },
    inProgress() {
      if (this.attachmentState) {
        return this.timeRemaining > 0 && !this.completed;
      }
    },
    name() {
      if (this.card) {
        return this.card.title;
      } else {
        return this.rowAttachment.assetTitle;
      }
    },
    rowAttachment() {
      if (this.attachment) {
        return this.attachment;
      } else if (this.card) {
        return this.$store.getters['attachments/get'][this.card.assetId];
      }
    },
    locked() {
      if (this.card || !this.showLock) {
        return false;
      }

      return this.rowAttachment?.locked(this.$access, this.$store);
    },
    showPlay() {
      if (this.rowAttachment) {
        return this.rowAttachment.mediaType !== 'image' && this.rowAttachment.mediaType !== 'biodigital';
      }
    },
    timeRemaining() {
      if (this.attachmentState) {
        return this.rowAttachment.metadata?.duration - this.attachmentState.currentTimestamp;
      }
    },
    thumbnail() {
      let url;
      if (this.card) {
        url = this.$store.getters['attachments/get'][this.card.featuredImageId]?.smallThumbnail;
      } else {
        url = this.displayAttachment?.smallThumbnail;
      }

      if (url) {
        return `url("${url}")`;
      }
    },
    user() {
      return this.$store.getters['session/currentUser'];
    },
  },
  methods: {
    formatDuration(duration) {
      return Attachment.formatDuration(duration, true);
    },
    rowClick() {
      const param = this.card ? this.card : this.rowAttachment;
      this.$emit('row-clicked', param);
    },
    toggleBookmark() {
      this.$emit('toggle-bookmark');
    },
  },
};
</script>

<style lang="scss" scoped>
.action-container {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: rem-calc(40);
  gap: 0.25rem;
  border: 1.5px solid var(--border-secondary);
  padding: var(--spacing-xxs) var(--spacing-md);
  border-radius: var(--radius-full);

  .duration-text {
    color: var(--utility-gray-600);
  }

  .icon {
    max-height: 9.1px;
    max-width: 7.1px;
  }
}

.row-content {
  display: flex;
  align-items: center;
  flex: 1;
  gap: inherit;
}

.bookmark-icon {
  height: 24px;
}

.name {
  flex: 1;
  text-align: left;
  color: var(--text-primary-900);
  font-weight: var(--Fontweightfont-weight-medium);
  -webkit-line-clamp: 1;
  line-clamp: 1;
}

.name-container {
  .lock-button {
    margin-right: 6px;
  }
}
</style>
